'use client';

import { AttachmentPrimitive, ComposerPrimitive, MessagePrimitive, useAui, useAuiState } from '@assistant-ui/react';
import { PermissionKey } from '@schema/permission.schema';
import { trpc } from '@utils/trpc';
import { FileText, FolderOpen, Library, PlusIcon, UploadIcon, XIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { useShallow } from 'zustand/shallow';
import { TooltipIconButton } from '~/components/assistant-ui/TooltipIconButton';
import { cn } from '~/components/lib/utils';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '~/components/shadcn/Dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/shadcn/Tooltip';
import JobDocumentPicker from '~/components/assistant-ui/s3-documents/JobDocumentPicker';
import { useS3Docs } from '~/components/assistant-ui/s3-documents/S3DocContext';
import SelectFromDocumentHubModal from '~/components/pages/documents/SelectFromDocumentHubModal';
import { useUserContext } from '~/react_context/UserContext';
import { CHAT_ATTACHABLE_FILE_TYPES, CHAT_DOCUMENT_SELECTION_LIMIT } from '~/server/schema/chatDocuments';
import { canOpenDocuments } from '~/server/schema/documentVisibility';

const useFileSrc = (file: File | undefined) => {
	const [src, setSrc] = useState<string | undefined>(undefined);

	useEffect(() => {
		if (!file) {
			setSrc(undefined);
			return;
		}

		const objectUrl = URL.createObjectURL(file);
		setSrc(objectUrl);

		return () => {
			URL.revokeObjectURL(objectUrl);
		};
	}, [file]);

	return src;
};

const useAttachmentSrc = () => {
	const { file, src } = useAuiState(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		useShallow((s: any): { file?: File; src?: string } => {
			if (s.attachment.type !== 'image') return {};
			if (s.attachment.file) return { file: s.attachment.file };
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const src = s.attachment.content?.filter((c: any) => c.type === 'image')[0]?.image;
			if (!src) return {};
			return { src };
		})
	);

	return useFileSrc(file) ?? src;
};

type AttachmentPreviewProps = {
	src: string;
};

const AttachmentPreview: FC<AttachmentPreviewProps> = ({ src }) => {
	const [isLoaded, setIsLoaded] = useState(false);
	return (
		<img
			src={src}
			alt="Image Preview"
			className={cn(
				'block h-auto max-h-[80vh] w-auto max-w-full object-contain',
				isLoaded ? 'aui-attachment-preview-image-loaded' : 'aui-attachment-preview-image-loading invisible'
			)}
			onLoad={() => setIsLoaded(true)}
		/>
	);
};

const AttachmentPreviewDialog: FC<PropsWithChildren> = ({ children }) => {
	const src = useAttachmentSrc();

	if (!src) return children;

	return (
		<Dialog>
			<DialogTrigger className="aui-attachment-preview-trigger cursor-pointer transition-colors hover:bg-accent/50" asChild>
				{children}
			</DialogTrigger>
			<DialogContent className="aui-attachment-preview-dialog-content p-2 sm:max-w-3xl [&>button]:rounded-full [&>button]:bg-foreground/60 [&>button]:p-1 [&>button]:opacity-100 [&>button]:ring-0! [&_svg]:text-background [&>button]:hover:[&_svg]:text-destructive">
				<DialogTitle className="aui-sr-only sr-only">Image Attachment Preview</DialogTitle>
				<div className="aui-attachment-preview relative mx-auto flex max-h-[80dvh] w-full items-center justify-center overflow-hidden bg-background">
					<AttachmentPreview src={src} />
				</div>
			</DialogContent>
		</Dialog>
	);
};

const AttachmentThumb: FC = () => {
	const src = useAttachmentSrc();

	if (src) {
		return <img src={src} alt="Attachment preview" className="h-full w-full rounded-lg object-cover" />;
	}

	return (
		<div className="flex h-full w-full items-center justify-center rounded-lg bg-muted">
			<FileText className="size-6 text-muted-foreground" />
		</div>
	);
};

const AttachmentUI: FC = () => {
	const aui = useAui();
	// @ts-ignore — ScopeRegistry augmentation not resolved with moduleResolution "node"
	const isComposer = aui.attachment.source === 'composer';

	// @ts-ignore — ScopeRegistry augmentation not resolved with moduleResolution "node"
	const isImage = useAuiState((s) => s.attachment.type === 'image');
	// @ts-ignore — ScopeRegistry augmentation not resolved with moduleResolution "node"
	const fileName = useAuiState((s) => s.attachment.name as string | undefined);
	const typeLabel = useAuiState((s) => {
		// @ts-ignore — ScopeRegistry augmentation not resolved with moduleResolution "node"
		const type = s.attachment.type;
		switch (type) {
			case 'image':
				return 'Image';
			case 'document':
				return 'Document';
			case 'file':
				return 'File';
			default:
				return type;
		}
	});

	if (isImage) {
		return (
			<Tooltip>
				<AttachmentPrimitive.Root className="aui-attachment-root relative">
					<AttachmentPreviewDialog>
						<TooltipTrigger asChild>
							<div
								className={cn(
									'aui-attachment-tile size-16 cursor-pointer overflow-hidden rounded-xl border bg-muted transition-opacity hover:opacity-75',
									isComposer && 'border-foreground/20'
								)}
								aria-label={`${typeLabel} attachment`}
								role="button"
							>
								<AttachmentThumb />
							</div>
						</TooltipTrigger>
					</AttachmentPreviewDialog>
					{isComposer && <AttachmentRemove />}
				</AttachmentPrimitive.Root>
				<TooltipContent side="top">
					<AttachmentPrimitive.Name />
				</TooltipContent>
			</Tooltip>
		);
	}

	return (
		<AttachmentPrimitive.Root className="aui-attachment-root relative">
			<div
				className={cn(
					'aui-attachment-tile flex h-16 max-w-48 cursor-pointer items-center gap-2 overflow-hidden rounded-xl border bg-muted px-3 transition-opacity hover:opacity-75',
					isComposer && 'border-foreground/20'
				)}
				aria-label={`${typeLabel} attachment`}
				role="button"
			>
				<FileText className="size-5 shrink-0 text-muted-foreground" />
				<span className="truncate text-sm text-foreground">{fileName ?? 'File'}</span>
			</div>
			{isComposer && <AttachmentRemove />}
		</AttachmentPrimitive.Root>
	);
};

const AttachmentRemove: FC = () => {
	return (
		<AttachmentPrimitive.Remove asChild>
			<TooltipIconButton
				tooltip="Remove file"
				className="aui-attachment-tile-remove absolute top-1.5 right-1.5 size-3.5 rounded-full bg-white opacity-100 shadow-sm hover:bg-white! [&_svg]:text-destructive"
				side="top"
			>
				<XIcon className="aui-attachment-remove-icon size-3 dark:stroke-[2.5px]" />
			</TooltipIconButton>
		</AttachmentPrimitive.Remove>
	);
};

export const UserMessageAttachments: FC = () => {
	return (
		<div className="aui-user-message-attachments-end col-span-full col-start-1 row-start-1 flex w-full flex-row justify-end gap-2">
			<MessagePrimitive.Attachments components={{ Attachment: AttachmentUI }} />
		</div>
	);
};

export const ComposerAttachments: FC = () => {
	return (
		<div className="aui-composer-attachments mb-2 flex w-full flex-row items-center gap-2 overflow-x-auto px-1.5 pt-0.5 pb-1 empty:hidden">
			<ComposerPrimitive.Attachments components={{ Attachment: AttachmentUI }} />
		</div>
	);
};

export const ComposerAddAttachment: FC = () => {
	const { addDocuments } = useS3Docs();
	const { allowedPermissions, role } = useUserContext();
	const utils = trpc.useUtils();
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const [hubPickerOpen, setHubPickerOpen] = useState(false);
	const [pickerOpen, setPickerOpen] = useState(false);
	const canUseDocumentHub = canOpenDocuments(role) && allowedPermissions.includes(PermissionKey.MENU_DOCUMENT_HUB);
	const uploadRef = useRef<HTMLButtonElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Close dropdown on outside click
	useEffect(() => {
		if (!dropdownOpen) return;
		const handler = (e: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
				setDropdownOpen(false);
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [dropdownOpen]);

	const handleUploadFromComputer = useCallback(() => {
		setDropdownOpen(false);
		// ComposerPrimitive.AddAttachment wires the browser file picker to its
		// own click handler internally. We can't render it as the dropdown item
		// directly because it must stay mounted (hidden) while the dropdown
		// opens/closes. Instead we keep a hidden button and forward the click.
		uploadRef.current?.click();
	}, []);

	const handleFromJob = useCallback(() => {
		setDropdownOpen(false);
		setPickerOpen(true);
	}, []);

	const handleFromDocumentHub = useCallback(() => {
		setDropdownOpen(false);
		setHubPickerOpen(true);
	}, []);

	const attachFromDocumentHub = useCallback(
		async (selection: { documentIds: number[]; folderIds: number[] }) => {
			const { documents, truncated } = await utils.documents.documentHubSelectionForChat.fetch(selection);
			if (documents.length === 0) {
				toast.info('None of the selected files can be read by the assistant.');
				return;
			}
			if (truncated) {
				toast.info(`Only the first ${CHAT_DOCUMENT_SELECTION_LIMIT} files were attached.`);
			}
			addDocuments(documents);
		},
		[addDocuments, utils]
	);

	return (
		<div className="relative" ref={dropdownRef}>
			{/* Hidden native file picker trigger */}
			<ComposerPrimitive.AddAttachment asChild>
				<button ref={uploadRef} className="hidden" aria-hidden="true" tabIndex={-1} type="button" />
			</ComposerPrimitive.AddAttachment>

			{/* Visible "+" button */}
			<TooltipIconButton
				tooltip="Add Attachment"
				side="bottom"
				variant="ghost"
				size="icon"
				className="aui-composer-add-attachment size-8.5 rounded-full p-1 font-semibold text-xs hover:bg-muted-foreground/15 dark:border-muted-foreground/15 dark:hover:bg-muted-foreground/30"
				aria-label="Add Attachment"
				onClick={() => setDropdownOpen((v) => !v)}
			>
				<PlusIcon className="aui-attachment-add-icon size-5 stroke-[1.5px]" />
			</TooltipIconButton>

			{/* Dropdown menu */}
			{dropdownOpen && (
				<div className="absolute bottom-full left-0 z-[10000] mb-1 w-max overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
					<button
						className="flex w-full cursor-pointer select-none items-center gap-2 whitespace-nowrap rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
						onClick={handleUploadFromComputer}
						type="button"
					>
						<UploadIcon className="size-4" />
						Upload from computer
					</button>
					<button
						className="flex w-full cursor-pointer select-none items-center gap-2 whitespace-nowrap rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
						onClick={handleFromJob}
						type="button"
					>
						<FolderOpen className="size-4" />
						From a job
					</button>
					{canUseDocumentHub && (
						<button
							className="flex w-full cursor-pointer select-none items-center gap-2 whitespace-nowrap rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
							onClick={handleFromDocumentHub}
							type="button"
						>
							<Library className="size-4" />
							From Document Hub
						</button>
					)}
				</div>
			)}

			{/* Job document picker dialog */}
			<JobDocumentPicker isOpen={pickerOpen} onAttach={addDocuments} onClose={() => setPickerOpen(false)} />

			{canUseDocumentHub && (
				<SelectFromDocumentHubModal
					fileTypeFilter={CHAT_ATTACHABLE_FILE_TYPES}
					isOpen={hubPickerOpen}
					onItemsSelected={attachFromDocumentHub}
					setIsOpen={setHubPickerOpen}
					zIndex="z-[10001]"
				/>
			)}
		</div>
	);
};
